import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "../redux/store";
import { updateAppointment } from "../redux/slices/appointmentSlice";
import { AppDispatch } from "../redux/store";
import { AppointmentState } from "../types";
import { sendToWindowDataLayer } from "../utils/dom";
import { SlotInfo } from "../types/available-slots.interface";
import {
  getShopNow,
  resolveSameDayBufferMinutes,
  toShopTime,
} from "../utils/sameDayBooking";

const DEBUG_MODE = false;

interface TimePickerProps {
  appointment: AppointmentState;
  selectedTime: string;
  setSelectedTime: (time: string) => void;
  setComplimentaryRide: (ride: string) => void;
  bookingTimes: SlotInfo[];
  loading: boolean;
  noSlotsMessage: string;
  setClear: (clear: boolean) => void;
  setBounceComplimentaryRide: (bounce: boolean) => void;
}

const TRANSPORT_TYPES = [
  { flag: "shuttleEnabled", label: "Shuttle" },
  { flag: "loanerEnabled", label: "Loaner" },
  { flag: "rentalEnabled", label: "Rental Car" },
] as const;

export const TimePicker: React.FC<TimePickerProps> = ({
  appointment,
  selectedTime,
  setSelectedTime,
  setComplimentaryRide,
  bookingTimes,
  loading,
  noSlotsMessage,
  setClear,
  setBounceComplimentaryRide,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const accentColor = appointment?.shopData?.color || "#2b73b9";

  // Filter out past time slots and same-day slot buffer minutes.
  const now = useMemo(
    () => getShopNow(appointment?.shopData),
    [appointment?.shopData]
  );
  const sameDayBufferMinutes = useMemo(() => {
    return resolveSameDayBufferMinutes(
      appointment?.shopData,
      appointment?.apptType
    );
  }, [appointment?.apptType, appointment?.shopData]);

  const futureSlots = useMemo(() => {
    if (!bookingTimes) return [];
    const cutoff = now.add(sameDayBufferMinutes, "minute");
    return bookingTimes.filter((slot) => {
      const slotStart = toShopTime(
        slot.startDateTime,
        appointment?.shopData
      );
      if (!slotStart.isValid()) {
        return true;
      }

      if (slotStart.isSame(now, "day")) {
        return slotStart.isAfter(cutoff);
      }

      return slotStart.isAfter(now);
    });
  }, [
    appointment?.shopData,
    bookingTimes,
    now,
    sameDayBufferMinutes,
  ]);

  const SLOTS_PER_PAGE = 12;
  const [currentPage, setCurrentPage] = useState(0);
  const totalPages = Math.ceil((futureSlots.length || 0) / SLOTS_PER_PAGE);
  const pagedSlots = futureSlots.slice(
    currentPage * SLOTS_PER_PAGE,
    currentPage * SLOTS_PER_PAGE + SLOTS_PER_PAGE
  );
  const placeholderCount = Math.max(0, SLOTS_PER_PAGE - pagedSlots.length);

  // Reset to first page when bookingTimes changes
  useEffect(() => {
    setCurrentPage(0);
  }, [bookingTimes]);

  const hasAnyTransportOption = useMemo(() => {
    const shop = appointment?.shopData || {};
    return TRANSPORT_TYPES.some((t) => shop[t.flag as keyof typeof shop]);
  }, [appointment?.shopData]);

  const formatTimeForCustomer = (timeString: string): string => {
    if (timeString.includes(" - ")) {
      return timeString.split(" - ")[0];
    }
    return timeString;
  };

  const renderTimeButton = (slot: SlotInfo) => {
    const isSelected = slot.time === selectedTime;
    const isDisabled = !slot.isAvailable || slot.hasBlocker;

    return (
      <div
        key={slot.time}
        className="relative group"
        onMouseEnter={() => {
          if (DEBUG_MODE) {
            console.log("SlotInfo for time:", slot.time, slot);
          }
        }}
      >
        <button
          type="button"
          disabled={isDisabled}
          className={`w-full rounded-xl py-3 px-3 text-sm font-semibold transition-all font-roboto ${
            isDisabled
              ? "opacity-40 cursor-not-allowed"
              : "cursor-pointer hover:scale-[1.02]"
          }`}
          onClick={() => {
            if (isDisabled) return;
            const isTimeChanged = slot.time !== selectedTime;
            const requiresComments = Boolean(appointment?.shopData?.requireComments);
            const shouldResetRide = hasAnyTransportOption && isTimeChanged;

            dispatch(
              updateAppointment({
                updates: { time: slot.time },
              })
            );
            setSelectedTime(slot.time);
            sendToWindowDataLayer("dc-service-time", slot.time);

            if (shouldResetRide) {
              dispatch(
                updateAppointment({
                  updates: { complimentaryRide: "" },
                })
              );
              setComplimentaryRide("");
              setBounceComplimentaryRide(false);
            }

            setClear(!requiresComments && !shouldResetRide);
          }}
          style={{
            backgroundColor: isSelected ? accentColor : "#ffffff",
            border: `2px solid ${accentColor}`,
            color: isSelected ? "#ffffff" : accentColor,
          }}
        >
          {formatTimeForCustomer(slot.time)}
        </button>
        {isDisabled && (
          <div className="hidden group-hover:block absolute z-50 bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-xs rounded shadow-lg bg-gray-800 text-white">
            Time slot is booked
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      id="time-picker"
      className="rounded-2xl bg-white p-5 font-roboto"
      style={{
        border: `2px solid ${accentColor}33`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      }}
    >
      <h3
        className="text-base font-bold text-center"
        style={{ color: "#1f2937" }}
      >
        Select a Time Slot
      </h3>
      <p className="text-xs text-gray-500 text-center mb-4">
        Time slots reflect shop hours
      </p>

      {loading ? (
        <div className="flex flex-col items-center justify-center text-center py-4">
          <i
            className="fa-solid fa-tire-rugged animate-spin text-5xl"
            aria-hidden="true"
            style={{ color: "#6b6b6b", lineHeight: 1 }}
          />
          <p className="text-sm text-gray-600 mt-3">
            Finding the best times for you!
          </p>
        </div>
      ) : futureSlots.length === 0 ? (
        <div className="py-4">
          <pre
            style={{
              fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
              whiteSpace: "pre-wrap",
              overflowWrap: "break-word",
              overflow: "auto",
              fontSize: "14px",
              color: "#4b5563",
            }}
          >
            {noSlotsMessage}
          </pre>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2">
            {pagedSlots.map((slot) => renderTimeButton(slot))}
            {Array.from({ length: placeholderCount }).map((_, index) => (
              <div key={`placeholder-${index}`} aria-hidden="true">
                <button
                  type="button"
                  tabIndex={-1}
                  disabled
                  className="w-full rounded-xl py-3 px-3 text-sm font-semibold font-roboto"
                  style={{ visibility: "hidden" }}
                >
                  Placeholder
                </button>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-4">
              <button
                type="button"
                className="flex items-center justify-center w-8 h-8 rounded-full transition-opacity focus:outline-none"
                style={{
                  border: "2px solid #d1d5db",
                  backgroundColor: currentPage > 0 ? accentColor : "transparent",
                  cursor: currentPage > 0 ? "pointer" : "not-allowed",
                  opacity: currentPage > 0 ? 1 : 0.4,
                }}
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                aria-label="Previous page"
              >
                <i
                  className="fa-solid fa-chevron-left"
                  aria-hidden="true"
                  style={{ fontSize: "12px", color: currentPage > 0 ? "#ffffff" : "#6b7280", lineHeight: 1 }}
                />
              </button>
              <span className="text-xs font-medium text-gray-500">
                {currentPage + 1} of {totalPages}
              </span>
              <button
                type="button"
                className="flex items-center justify-center w-8 h-8 rounded-full transition-opacity focus:outline-none"
                style={{
                  border: "2px solid #d1d5db",
                  backgroundColor: currentPage < totalPages - 1 ? accentColor : "transparent",
                  cursor: currentPage < totalPages - 1 ? "pointer" : "not-allowed",
                  opacity: currentPage < totalPages - 1 ? 1 : 0.4,
                }}
                onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={currentPage >= totalPages - 1}
                aria-label="Next page"
              >
                <i
                  className="fa-solid fa-chevron-right"
                  aria-hidden="true"
                  style={{
                    fontSize: "12px",
                    color: currentPage < totalPages - 1 ? "#ffffff" : "#6b7280",
                    lineHeight: 1,
                  }}
                />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
